import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ParticipantManager = ({ participants = [], onUpdate }) => {
  const [newParticipant, setNewParticipant] = useState({
    name: '',
    phone: '',
    contribution: 'meat',
    dietary: '',
    confirmed: false
  });

  const [showAddForm, setShowAddForm] = useState(false);

  const contributionTypes = [
    { value: 'meat', label: 'Carne', icon: 'Beef' },
    { value: 'beverages', label: 'Bebidas', icon: 'Coffee' },
    { value: 'sides', label: 'Acompanhamentos', icon: 'Salad' },
    { value: 'equipment', label: 'Equipamentos', icon: 'Wrench' },
    { value: 'money', label: 'Dinheiro', icon: 'DollarSign' }
  ];

  const handleAddParticipant = () => {
    if (!newParticipant?.name?.trim()) return;
    
    const participant = {
      id: Date.now(),
      ...newParticipant,
      name: newParticipant?.name?.trim()
    };
    
    onUpdate([...participants, participant]);
    setNewParticipant({
      name: '',
      phone: '',
      contribution: 'meat',
      dietary: '',
      confirmed: false
    });
    setShowAddForm(false);
  };

  const handleUpdateParticipant = (id, field, value) => {
    const updated = participants?.map(p => 
      p?.id === id ? { ...p, [field]: value } : p
    );
    onUpdate(updated);
  };

  const handleRemoveParticipant = (id) => {
    const updated = participants?.filter(p => p?.id !== id);
    onUpdate(updated);
  };

  const confirmedCount = participants?.filter(p => p?.confirmed)?.length || 0;
  const pendingCount = participants?.length - confirmedCount;

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-primary/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="Users" size={20} className="text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-xl font-bold text-primary">{participants?.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-success/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <div>
              <p className="text-sm text-muted-foreground">Confirmados</p>
              <p className="text-xl font-bold text-success">{confirmedCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-warning/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={20} className="text-warning" />
            <div>
              <p className="text-sm text-muted-foreground">Pendentes</p>
              <p className="text-xl font-bold text-warning">{pendingCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="Utensils" size={20} className="text-secondary" />
            <div>
              <p className="text-sm text-muted-foreground">Contribuições</p>
              <p className="text-xl font-bold text-secondary">
                {new Set(participants?.map(p => p?.contribution))?.size}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Add Participant */}
      <div className="bg-muted/50 rounded-lg p-4">
        {!showAddForm ? (
          <Button
            variant="outline"
            onClick={() => setShowAddForm(true)}
            iconName="Plus"
            iconPosition="left"
            fullWidth
            className="border-dashed"
          >
            Adicionar Participante
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground">Novo Participante</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowAddForm(false)}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome"
                value={newParticipant?.name}
                onChange={(e) => setNewParticipant(prev => ({
                  ...prev,
                  name: e?.target?.value
                }))}
                placeholder="Nome do participante"
                required
              />
              
              <Input
                label="Telefone"
                value={newParticipant?.phone}
                onChange={(e) => setNewParticipant(prev => ({
                  ...prev,
                  phone: e?.target?.value
                }))}
                placeholder="(11) 99999-9999"
              />
              
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Contribuição
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {contributionTypes?.map((type) => (
                    <button
                      key={type?.value}
                      onClick={() => setNewParticipant(prev => ({
                        ...prev,
                        contribution: type?.value
                      }))}
                      className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                        newParticipant?.contribution === type?.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border border-border hover:bg-muted'
                      }`}
                    >
                      <Icon name={type?.icon} size={16} />
                      {type?.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <Input
                label="Restrições Alimentares"
                value={newParticipant?.dietary}
                onChange={(e) => setNewParticipant(prev => ({
                  ...prev,
                  dietary: e?.target?.value
                }))}
                placeholder="Ex: Vegetariano, alergia, etc."
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleAddParticipant}
                disabled={!newParticipant?.name?.trim()}
                iconName="Plus"
                iconPosition="left"
              >
                Adicionar
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAddForm(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Participants List */}
      <div className="space-y-3">
        <h3 className="font-medium text-foreground">Participantes ({participants?.length})</h3>
        
        {participants?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Users" size={48} className="mx-auto mb-2 opacity-50" />
            <p>Nenhum participante adicionado ainda</p>
            <p className="text-sm">Clique em "Adicionar Participante" para começar</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {participants?.map((participant) => (
              <div
                key={participant?.id}
                className="bg-card rounded-lg border border-border p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">
                          {participant?.name}
                        </h4>
                        {participant?.phone && (
                          <p className="text-sm text-muted-foreground">
                            {participant?.phone}
                          </p>
                        )}
                      </div>
                      
                      {/* Confirmation Toggle */}
                      <button
                        onClick={() => handleUpdateParticipant(
                          participant?.id,
                          'confirmed',
                          !participant?.confirmed
                        )}
                        className={`p-1 rounded-full transition-colors ${
                          participant?.confirmed
                            ? 'text-success hover:bg-success/10' :'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon 
                          name={participant?.confirmed ? "CheckCircle" : "Circle"} 
                          size={20} 
                        />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Contribution */}
                      <div>
                        <label className="text-sm text-muted-foreground">Contribuição</label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {contributionTypes?.map((type) => (
                            <button
                              key={type?.value}
                              onClick={() => handleUpdateParticipant(
                                participant?.id,
                                'contribution',
                                type?.value
                              )}
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                                participant?.contribution === type?.value
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
                              }`}
                            >
                              <Icon name={type?.icon} size={12} />
                              {type?.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Dietary Restrictions */}
                      <div>
                        <label className="text-sm text-muted-foreground">Restrições</label>
                        <Input
                          value={participant?.dietary || ''}
                          onChange={(e) => handleUpdateParticipant(
                            participant?.id,
                            'dietary',
                            e?.target?.value
                          )}
                          placeholder="Nenhuma"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveParticipant(participant?.id)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Quick Actions */}
      {participants?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const allConfirmed = participants?.every(p => p?.confirmed);
              const updated = participants?.map(p => ({
                ...p,
                confirmed: !allConfirmed
              }));
              onUpdate(updated);
            }}
            iconName="CheckCircle"
          >
            {participants?.every(p => p?.confirmed) ? 'Desmarcar Todos' : 'Confirmar Todos'}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const phoneList = participants
                ?.filter(p => p?.phone)
                ?.map(p => p?.phone)
                ?.join(', ');
              if (phoneList) {
                navigator.clipboard?.writeText(phoneList);
                alert('Lista de telefones copiada!');
              }
            }}
            iconName="Copy"
          >
            Copiar Telefones
          </Button>
        </div>
      )}
    </div>
  );
};

export default ParticipantManager;